//@ts-ignore
import store from '../../../store';
import {ZoomCommand} from './enums/ZoomCommand';
import {IRectangleScaledNoAxis} from './interfaces/primitives/IRectangleScaledNoAxis';
import {DropZone} from './enums/DropZone';
import {GameScene} from './pixi_scene/GameScene';
import {Conversion} from './utils/Conversion';
import {StaticData} from './data/StaticData';
import {DynamicData} from './data/DynamicData';
import {ResizeDebounceController} from './controllers/silent/ResizeDebounceController';
import {PixiApplicationController} from './controllers/PixiApplicationController';
import {ActiveData} from './data/ActiveData';
import {IJsonPatch} from './data/active_data/base/static_config_data/JsonPatch';
import {ActiveDataCommandsController} from './controllers/ActiveDataCommandsController';
import {CurrentGame, Player} from '../../../../types';
import {NewPixiSceneEvents} from './events/NewPixiSceneEvents';
import {Point} from 'pixi.js';
import {TableController} from './controllers/silent/TableController';
import {CardSelector} from './controllers/CardSelector';
import {SceneBorder} from './pixi_scene/game_scene/SceneBorder';
import {SimpleRoundedRect} from './pixi_scene/game_scene/SimpleRoundedRect';
import MessageHelper from '../../../helpers/MessageHelper';
import {GamesStrategiesController} from './games/GamesStrategiesController';
import {GameStrategy} from './games/strategies/base/GameStrategy';
import {TextureUrlLoader} from './utils/TextureUrlLoader';
import {TextFactory} from './factories/TextFactory';
import * as WebFont from 'webfontloader';
import {IOperationItem} from './controllers/active_data_commands_controller/IOperationItem';
import {ActiveDataCommands} from './controllers/active_data_commands_controller/ActiveDataCommands';
import EventApi from '../../../api/EventsApi';
import {GameState} from '../../../../types/game-state';
import TState = GameState.TState;

export class NewPixiScene
{
    private static instance: NewPixiScene;
    sceneBorder: SceneBorder;
    sceneMask: SimpleRoundedRect;
    private readonly gameScene: GameScene;

    constructor(rect: DOMRect, isAdmin: boolean, players: Player[]) {
        console.log('ADMIN', isAdmin);
        DynamicData.sceneSizes = rect;
        DynamicData.handSizes = new DOMRect();
        DynamicData.players = players;
        DynamicData.applyDefaultPermissions();
        StaticData.isAdmin = isAdmin;
        PixiApplicationController.create();
        ActiveData.createClasses();
        this.sceneBorder = new SceneBorder();
        PixiApplicationController.app.stage.addChild(this.sceneBorder);
        this.gameScene = new GameScene();
        PixiApplicationController.app.stage.addChild(this.gameScene);
        ResizeDebounceController.start(0, this.resize.bind(this));
        this.initialize();
        CardSelector.updateSelectFilterColor();

        this.sceneMask = new SimpleRoundedRect();
        PixiApplicationController.app.stage.addChild(this.sceneMask);
        this.gameScene.mask = this.sceneMask;
    }

    get view(): HTMLCanvasElement {
        return PixiApplicationController.app!.view;
    }

    private _loaded: boolean = false;

    get loaded(): boolean {
        return this._loaded;
    }

    private _guest: boolean = false;

    set guest(value: boolean) {
        this._guest = value;
    }

    /**
     * Установка цвета границы игрового поля
     *  @param value - цвет границы в формате 0x0 - 0xffffff или "#000" - "#ffffff"
     */
    set borderColor(value: number | string) {
        let color: number = (typeof value === 'number') ? value : parseInt(value.slice(1), 16);
        this.sceneBorder.setColor(color);
    }

    /**
     * Получить актуальную позицию и размер игрового поля
     */
    get tableCameraParams(): IRectangleScaledNoAxis {
        return {
            x: this.gameScene.gameTable.x,
            y: this.gameScene.gameTable.y,
            scale: this.gameScene.gameTable.scale.x,
            width: DynamicData.sceneSizes.width,
            height: DynamicData.sceneSizes.height
        };
    }

    /**
     * Создаём экземпляр себя
     * @param rect - область, отведённая под pixi-контейнер
     * @param isAdmin - флаг, является ли текущий пользователь админом
     * @param translations - таблица локализации
     * @param playerId - ID локального игрока
     * @param players - Список игроков
     */
    static createInstance(
        rect: DOMRect,
        isAdmin: boolean,
        translations: {[p: string]: string},
        playerId: string,
        players: Array<Player>
    ): NewPixiScene {
        console.log('Create scene', Date.now());
        StaticData.playerId = playerId;
        StaticData.translationsMap = Conversion.ObjectWithKeysToMap<string>(translations);
        if (!NewPixiScene.instance) {
            NewPixiScene.instance = new NewPixiScene(rect, isAdmin, players);
        }
        return NewPixiScene.instance;
    }

    /**
     * Обновить список игроков
     * @param players
     */
    updatePlayers(players: Array<Player>): void {
        DynamicData.players = players;
        this.updatePlayerColor();
        //this.resize();
    }

    /**
     * Показать/скрыть руку
     * @param handIsOpened - показана ли рука
     */
    toggleHand(handIsOpened: boolean): void {
        this.gameScene.toggleHand(StaticData.isAdmin ? true : handIsOpened);
    };

    /**
     * Игра перезагружается
     */
    public restartGame(): void {
        //this.isRestarting = true;
    }

    getHoverSrc(): any {
        //фикс для KeyboardEventsObserver:20
        return null;
    }

    /**
     * Установка актуального цвета игрока
     */
    updatePlayerColor(): void {
        dispatchEvent(new MessageEvent(NewPixiSceneEvents.PLAYERS_UPDATED, {data: null}));
        CardSelector.updateSelectFilterColor();
        this.gameScene.gameTable.tableObjects?.videoPlaceholders?.updateColors();
    };

    /**
     * Перерисовка при изменении размеров окна
     *  @param fromLoader - выполняется ли из прелодера
     */
    resize(): void {
        const el = document.getElementById('tableStageNew');
        if (el?.clientWidth && el?.clientHeight) {
            DynamicData.sceneSizes.width = el?.clientWidth;
            DynamicData.sceneSizes.height = el?.clientHeight;
            DynamicData.handSizes.height = Math.floor(Math.max(DynamicData.sceneSizes.height / 5, 50)) - StaticData.sceneBorderThickness;
            DynamicData.handSizes.width = DynamicData.sceneSizes.width - StaticData.sceneBorderThickness * 2;
            PixiApplicationController.resize();
            this.gameScene.resize();
            this.sceneBorder.updateDimensions();
            this.sceneMask.draw(DynamicData.sceneSizes.width, DynamicData.sceneSizes.height, this.sceneMask.color, this.sceneMask.radius, StaticData.sceneBorderThickness);
        }
    };

    /**
     * Перерисовка при изменении размеров окна
     *  @param fromLoader - выполняется ли из прелодера
     */
    applyJsonPatch(patchArray: Array<IJsonPatch>): void {
        ActiveData.applyJsonPatchArray(patchArray);
    };

    /**
     * Изменение масштаба игрового поля
     *  @param command - команда масштабирования: "+" - увеличить,"-" - уменьшить, "*" - исходное состояние
     */
    zoom(command: string): void {
        this.gameScene.gameTable.zoom(command as ZoomCommand);
    };

    changeAdminDebugVisibility(value: boolean): void {
        this.gameScene.gameTable.background.setGridVisible(value);
    }

    getDropZone(y: number): DropZone {
        return DropZone.TABLE;
    }

    gameStateHandler(gameState: TState): void {
        //game strategies apply
        GamesStrategiesController.disable();
        let strategy: GameStrategy | undefined = GamesStrategiesController.getStrategy(gameState);
        strategy?.execute(gameState);

        //permissions apply
        DynamicData.applyDefaultPermissions();
        const currentActivity: null | CurrentGame = store.getters.getCurrentActivity;
        if (currentActivity) {
            // @ts-ignore
            DynamicData.settings = currentActivity.settings;
        }

        //videoseats update
        TableController.table.tableObjects?.tryAddVideoPlaceholders();
        TableController.table.tableObjects?.videoPlaceholders?.setPlayersData(gameState);
    }

    /**
     * Обработка диффов
     */
    diffHandler(data: Array<any>): void {
        if (!data) {
            return;
        }
        if (data.find((operationItem: IOperationItem) => operationItem.operation == ActiveDataCommands.CLEAR_FIELD)) {
            this._loaded = false;
        }
        ActiveDataCommandsController.diffHandler(data);
    }

    async gameFieldHandler(): Promise<void> {
        if (!DynamicData.fieldClear) {
            return;
        }
        this._loaded = false;
        this.showLoader();
        console.log('showing Loader PIXI', this._loaded, Date.now());
        DynamicData.fieldClear = false;
        await this.loadFont();
        ActiveData.createClasses();
        let urls: string[] = ActiveData.root.allPicturesUrls;
        for (let urlIndex: number = 0; urlIndex < urls.length; urlIndex++) {
            await TextureUrlLoader.load(urls[urlIndex]);
        }
        await this.gameScene.initialize();
        this.resize();
        this._loaded = true;
        this.hideLoader();
        console.log('hiding Loader PIXI', this._loaded, Date.now());
        const currentActivity: null | CurrentGame = store.getters.getCurrentActivity;
        if (currentActivity) {
            if (currentActivity.automation_code) {
                MessageHelper.playerLoaderFinished({
                    'activityId': currentActivity.id
                });
            } else {
                MessageHelper.loaderFinished({
                    'activityId': currentActivity.id
                });
            }
            MessageHelper.getState();
        }
    }

    /**
     * Центр сцены на столе
     */
    getSceneCenter(): Point {
        return TableController.getCenter();
    }

    private hideLoader(): void {
        EventApi.triggerEvent(EventApi.TABLE_IS_LOADED);
    };

    private showLoader(): void {
        EventApi.triggerEvent(EventApi.SHOW_TABLE_LOADER);
    };

    /**
     * Создание игрового поля npm install --save @types/webfontloader
     */
    private async initialize(): Promise<void> {
        await this.gameScene.initialize();
        await this.gameFieldHandler();
    }

    private async loadFont(): Promise<void> {
        await new Promise(resolve => {
            WebFont.load(
                {
                    // this event is triggered when the fonts have been rendered, see https://github.com/typekit/webfontloader
                    active: () => resolve(),

                    // when font is loaded do some magic, so font can be correctly rendered immediately after PIXI is initialized
                    fontloading: doMagic,

                    // multiple fonts can be passed here
                    google:
                        {
                            families: [TextFactory.DEFAULT_FONT + ':' + TextFactory.DEFAULT_FONT_WEIGHT + ':latin,cyrillic'/*, Settings.CITYNAME_FONT + ':700:latin,cyrillic'*/]
                        }
                });
        });

        function doMagic(familyName: string) {
            // create <p> tag with our font and render some text secretly. We don't need to see it after all...

            let el = document.createElement('p');
            el.style.fontFamily = familyName;
            el.style.fontSize = '0px';
            el.style.visibility = 'hidden';
            el.style.position = 'absolute';
            el.style.fontWeight = 'bold';
            el.innerHTML = StaticData.charsAndNumbers;

            document.body.appendChild(el);
            console.log('Magic');
        }
    }
}
